// src/components/controls/TimeRangeSelector.jsx
import React, { useState } from 'react';
import { Select } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar, Clock } from 'lucide-react';

const TimeRangeSelector = ({ value, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState({
    start: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    end: new Date().toISOString().slice(0, 16)
  });

  const presetRanges = [
    { value: '1h', label: 'Last Hour' },
    { value: '3h', label: 'Last 3 Hours' },
    { value: '6h', label: 'Last 6 Hours' },
    { value: '12h', label: 'Last 12 Hours' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '2d', label: 'Last 2 Days' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleCustomRangeChange = () => {
    // Validate the range
    const start = new Date(customRange.start);
    const end = new Date(customRange.end);
    
    if (start >= end) {
      alert('Start time must be before end time');
      return;
    }

    // Pass the custom range object directly
    onChange({
      start: customRange.start,
      end: customRange.end
    });
  };

  const handlePresetChange = (e) => {
    const newValue = e.target.value;
    setShowCustom(newValue === 'custom');
    if (newValue !== 'custom') {
      onChange(newValue);
    }
  };

  // Determine if current value is a custom range
  const isCustomRange = typeof value === 'object';

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label htmlFor="timeRange" className="text-sm font-medium text-gray-700 min-w-24">
              Time Range:
            </label>
            <Select 
              id="timeRange"
              value={isCustomRange ? 'custom' : value}
              onChange={handlePresetChange}
              className="flex-1"
            >
              {presetRanges.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </Select>
          </div>

          {showCustom && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Start Time
                  </label>
                  <input
                    type="datetime-local"
                    value={customRange.start}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                    max={new Date().toISOString().slice(0, 16)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    value={customRange.end}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                    max={new Date().toISOString().slice(0, 16)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCustom(false);
                    onChange('24h');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCustomRangeChange}
                >
                  Apply Range
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange('1h')}
            >
              1h
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange('6h')}
            >
              6h
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange('24h')}
            >
              24h
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange('7d')}
            >
              7d
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TimeRangeSelector;